// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { observable, action, makeAutoObservable } from "mobx";
import { MyTimApi } from "../api/my-tim.api";
import { MyTibApi } from "../api/my-tib.api";
import { Tim } from "../models";
import { TimInformation } from "../models/TimInfo.model";
import { NextTravelApi } from "../api/next-travel.api";
import _ from "lodash";
import { getClaimState, parseAmount, parseDate } from "../utils/parsers";
import i18n from 'i18next';

export default class MyTimStore {
  @observable card: Tim = {};
  @observable cardInfo: TimInformation = {};
  @observable claims: Array<any> = [];
  @observable removedClaims: Array<any> = [];
  @observable walletOperations: Array<any> = [];
  @observable headersWalletOpertations: any = {};
  @observable capping: any = {};
  @observable walletInfo: any = {};

  constructor() {
    makeAutoObservable(this);
  }
 
  @action getCard = async () => {
    this.card = await MyTimApi.getCard();
  };

  @action getCardInfo = async (tim: string) => {
    this.cardInfo = await MyTimApi.getCardInfo(tim);
  };

  @action getCappingValue = async () => {
    const curWalletInfo = await NextTravelApi.getMyWalletInfo();
    this.walletInfo = curWalletInfo;
    this.capping = curWalletInfo.capping;
  }

  @action getWalletOperations = async (page?: number, resultsPerPage?: number) => {
    const settings = await MyTibApi.getSettings();
    let response: any = await MyTimApi.getWalletOperations(page, resultsPerPage, settings.language);

    this.headersWalletOpertations = response.headers;

    const newWalletOperations = response && response.data ? response.data.map((item: any) => {
      let parsedInfo = JSON.parse(item.info);

      let hasPenalty: boolean = parsedInfo && parsedInfo.data.penalty.price > 0;
      let trips = [];
      if (hasPenalty) {
        let errorTrip = {
          type: `${i18n.t(parsedInfo.data.penalty.description.toLowerCase())} 
          (-${parseAmount(parsedInfo.data.penalty.price)})`,
        };
        trips.push(errorTrip);
      }

      let cancellations =
        parsedInfo &&
        parsedInfo.data.cancellations.map((trip: any) => {
          return {
            validationTime: parseDate(trip.validationTime, true),
            type: trip.type === "UP" ? i18n.t('entry') : i18n.t('exit'),
            lineCode: `L${trip.lineCode}`,
            address: `${trip.nucleus} (${trip.stopCode})`,
          };
        });

      const finalTrips = cancellations && cancellations.concat(trips);

      return {
        operationDate: parseDate(item.operationDate, true),
        error: hasPenalty,
        movements: parsedInfo
          ? `${parsedInfo.data.originNucleus} > ${parsedInfo.data.destinationNucleus}`
          : item.concept,
        tripHops: parsedInfo ? parsedInfo.data.jumps : "",
        amount: parseAmount(item.amount),
        totalAmount: parseAmount(item.totalAmount),
        points: item.points,
        totalPoints: item.totalPoints,
        info: { data: { cancellations: finalTrips } },
      };
    }) : [];

    if (this.walletOperations.length < 1) {
      this.walletOperations = newWalletOperations;
    } else {
      Array.prototype.push.apply(this.walletOperations, newWalletOperations);
    }
  };

  @action getClaims = async () => {
    let array = (await MyTimApi.getClaims())
      .map((e: any) => {
        return {
          ...e,
          state: parseInt(e.state)
        };
      });

    const translateType = (type: number) => {
      switch (type) {
        case 1:
          return i18n.t('claimsType.one');
        case 2:
          return i18n.t('claimsType.two');
        default:
          return null;
      }
    }

    let unfiltered: any = _.orderBy(
      array,
      ["state", "creationDate"],
      ["asc", "desc"]
    ).map((e: any) => {
      return {
        ...e,
        creationDate: parseDate(e.creationDate, true),
        notificationDate: e.notificationDate ? parseDate(e.notificationDate, true) : e.notificationDate,
        notificationType: translateType(e.notificationType),
        state: getClaimState(e.state)
      };
    });

    let removed: any = [];
    unfiltered.forEach((e: any, i: number) => {
      if (e.state === 3) {
        unfiltered.splice(i, 1);
        removed.push(e);
      }
    });
    this.setRemovedClaims(removed);
    const sorted = unfiltered.concat(removed);
    this.claims = sorted;
  };

  @action setRemovedClaims = (removed: any) => {
    this.removedClaims = removed;
  };
}
